import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Truck, CreditCard, BadgeCheck, Smartphone, Zap, Sun, Wrench } from "lucide-react";
import api, { money } from "@/lib/api";
import PublicLayout from "@/components/public/PublicLayout";
import ProductCard from "@/components/public/ProductCard";
import { Button } from "@/components/ui/button";

function Hero({ slides }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return;
    const dur = (Number(slides[i]?.duration) > 0 ? Number(slides[i].duration) : 5) * 1000;
    const t = setTimeout(() => setI((x) => (x + 1) % slides.length), dur);
    return () => clearTimeout(t);
  }, [i, slides]);
  if (!slides.length) return null;
  const idx = i % slides.length;
  const s = slides[idx];
  return (
    <section className="relative w-full overflow-hidden bg-background" data-testid="hero-slider">
      <div className="relative">
        <div className="relative mx-auto" style={{ maxWidth: Number(s.width) > 0 ? `${s.width}px` : undefined }}>
          <picture>
            <source media="(max-width: 640px)" srcSet={s.mobile_image || s.desktop_image} />
            <img src={s.desktop_image || s.mobile_image} alt="" className="block w-full object-contain" style={{ height: Number(s.height) > 0 ? `${s.height}px` : "auto", maxHeight: "80vh" }} />
          </picture>
          {s.button_text && (
            <div className="absolute bottom-3 left-3 sm:bottom-8 sm:left-8">
              <Button asChild size="lg" className="rounded-full shadow-lg" data-testid="hero-cta">
                <Link to={s.button_link || "/products"}>{s.button_text}<ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          )}
        </div>
        {slides.length > 1 && (
          <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2 sm:bottom-5">
            {slides.map((_, k) => (
              <button key={k} onClick={() => setI(k)} className={`h-2 rounded-full transition-all ${k === idx ? "w-8 bg-primary" : "w-2 bg-white/70 ring-1 ring-black/10"}`} aria-label={`Slide ${k + 1}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

const CAT_ICON = { mobile: Smartphone, ebike: Zap, solar: Sun, accessories: Wrench };

export default function Home() {
  const [data, setData] = useState({ hero: [], categories: [], brands: [], all: [], featured: [], latest: [], deals: [], plans: [], team: [], settings: {} });

  useEffect(() => {
    const grab = (key, url) => api.get(url).then((r) => setData((prev) => ({ ...prev, [key]: r.data }))).catch(() => {});
    grab("hero", "/public/hero");
    grab("categories", "/public/categories");
    grab("brands", "/public/brands");
    grab("all", "/public/products?limit=200");
    grab("featured", "/public/products?featured=true&limit=8");
    grab("latest", "/public/products?latest=true&limit=8");
    grab("deals", "/public/deals");
    grab("plans", "/public/plans");
    grab("team", "/public/team");
    grab("settings", "/public/settings");
  }, []);

  const symbol = data.settings?.currency?.symbol || "Rs.";
  const secMap = {};
  (data.settings?.sections || []).forEach((s) => { secMap[s.key] = s.visible !== false; });
  const vis = (key) => secMap[key] !== false;
  const heroSlides = data.hero.length
    ? data.hero
    : data.categories.map((c) => ({ desktop_image: c.image, mobile_image: c.image, button_text: `Shop ${c.name}`, button_link: `/products?category=${c.slug}`, duration: 4 }));

  return (
    <PublicLayout>
      {vis("hero") && <Hero slides={heroSlides} />}

      {/* Trust bar */}
      <section className="border-b border-border bg-card">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-6 py-8 md:grid-cols-4">
          {[[ShieldCheck, "Verified & Secure", "Backend-protected data"], [CreditCard, "Easy Installments", "3 to 36 months plans"], [Truck, "Fast Delivery", "Across Pakistan"], [BadgeCheck, "Official Warranty", "On all products"]].map(([Icon, t, d], k) => (
            <div key={k} className="flex items-center gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent text-accent-foreground"><Icon className="h-5 w-5" /></span>
              <div><p className="font-display text-sm font-semibold">{t}</p><p className="text-xs text-muted-foreground">{d}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      {vis("categories") && (
      <section className="mx-auto max-w-7xl px-6 py-10 sm:py-16" data-testid="section-categories">
        <div className="flex items-end justify-between">
          <div><span className="overline text-primary">Browse</span><h2 className="mt-2 font-display text-3xl font-bold tracking-tight lg:text-4xl">Featured Categories</h2></div>
          <Link to="/categories" className="hidden text-sm font-semibold text-primary hover:underline sm:block">View all →</Link>
        </div>
        <div className="mt-8 flex flex-wrap justify-center gap-6 sm:gap-8">
          {data.categories.map((c) => {
            const Icon = CAT_ICON[c.slug] || Smartphone;
            return (
              <Link key={c.id} to={`/products?category=${c.slug}`} data-testid={`category-${c.slug}`}
                className="group flex w-20 flex-col items-center gap-2 sm:w-24">
                <div className="relative h-20 w-20 overflow-hidden rounded-full border-2 border-border ring-2 ring-transparent transition-all duration-300 group-hover:border-primary group-hover:ring-primary/30 sm:h-24 sm:w-24">
                  <img src={c.image} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 grid h-6 w-6 place-items-center rounded-full bg-primary text-primary-foreground">
                    <Icon className="h-3 w-3" />
                  </span>
                </div>
                <p className="text-center text-xs font-semibold text-foreground transition-colors group-hover:text-primary sm:text-sm">{c.name}</p>
              </Link>
            );
          })}
        </div>
      </section>
      )}

      {/* Shop by Brand */}
      {vis("categories") && data.brands.length > 0 && (
      <section className="mx-auto max-w-7xl px-6 pb-4" data-testid="section-brands">
        <div><span className="overline text-primary">Top Brands</span><h2 className="mt-2 font-display text-3xl font-bold tracking-tight lg:text-4xl">Shop by Brand</h2></div>
        <div className="mt-8 flex flex-wrap justify-center gap-4 sm:gap-6">
          {data.brands.map((b) => (
            <Link key={b.id} to={`/products?brand=${encodeURIComponent(b.name)}`} data-testid={`brand-${b.slug || b.name}`}
              className="group flex w-24 flex-col items-center gap-2">
              <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-full border-2 border-border bg-card ring-2 ring-transparent transition-all duration-300 group-hover:border-primary group-hover:ring-primary/30 sm:h-24 sm:w-24">
                {b.logo
                  ? <img src={b.logo} alt={b.name} className="h-full w-full object-contain p-3 transition-transform duration-500 group-hover:scale-110" />
                  : <span className="font-display text-2xl font-extrabold text-primary">{(b.name || "?")[0]}</span>}
              </div>
              <p className="text-center text-xs font-semibold text-foreground transition-colors group-hover:text-primary sm:text-sm">{b.name}</p>
            </Link>
          ))}
        </div>
      </section>
      )}


      {/* Products organized by category */}
      {data.categories.map((cat) => {
        const items = (data.all || []).filter((p) => p.category_slug === cat.slug).slice(0, 4);
        if (!items.length) return null;
        return (
          <section key={cat.id} className="mx-auto max-w-7xl px-6 py-8" data-testid={`section-cat-${cat.slug}`}>
            <div className="flex items-end justify-between">
              <h2 className="font-display text-2xl font-bold tracking-tight lg:text-3xl">{cat.name}</h2>
              <Link to={`/products?category=${cat.slug}`} className="text-sm font-semibold text-primary hover:underline" data-testid={`cat-viewall-${cat.slug}`}>View all →</Link>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
              {items.map((p) => <ProductCard key={p.id} p={p} symbol={symbol} />)}
            </div>
          </section>
        );
      })}

      {/* Featured products */}
      {vis("featured") && data.featured.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-8" data-testid="section-featured">
          <span className="overline text-primary">Trending</span>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight lg:text-4xl">Featured Products</h2>
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
            {data.featured.map((p) => <ProductCard key={p.id} p={p} symbol={symbol} />)}
          </div>
        </section>
      )}

      {/* Deals */}
      {vis("deals") && data.deals.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-16" data-testid="section-deals">
          <span className="overline text-primary">Limited Time</span>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight lg:text-4xl">Deals & Offers</h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {data.deals.map((d) => (
              <div key={d.id} className="relative overflow-hidden rounded-2xl border border-border">
                <img src={d.image} alt={d.title} className="h-56 w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/20" />
                <div className="absolute inset-0 flex flex-col justify-center p-8 text-white">
                  {d.discount_percent > 0 && <span className="mb-2 w-fit rounded-full bg-primary px-3 py-1 text-xs font-bold">{d.discount_percent}% OFF</span>}
                  <h3 className="font-display text-2xl font-bold">{d.title}</h3>
                  <p className="mt-1 text-sm text-white/80">{d.description}</p>
                  <Button asChild size="sm" className="mt-4 w-fit rounded-full"><Link to={d.button_link || "/deals"}>{d.button_text || "Grab Deal"}</Link></Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Installment plans */}
      {vis("plans") && (
      <section className="bg-secondary/40 py-16" data-testid="section-plans">
        <div className="mx-auto max-w-7xl px-6">
          <span className="overline text-primary">Flexible</span>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight lg:text-4xl">Installment Plans</h2>
          <p className="mt-2 max-w-xl text-muted-foreground">Choose the duration that fits your budget. All plans in PKR with transparent charges calculated securely.</p>
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-7">
            {data.plans.map((p) => (
              <div key={p.id} className="rounded-xl border border-border bg-card p-4 text-center">
                <p className="font-display text-3xl font-extrabold text-primary">{p.duration_months}</p>
                <p className="text-xs font-medium text-muted-foreground">Months</p>
        </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* How it works */}
      {vis("how") && (
      <section className="mx-auto max-w-7xl px-6 py-16" data-testid="section-how">
        <span className="overline text-primary">Simple Process</span>
        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight lg:text-4xl">How It Works</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-4">
          {[["Browse & Choose", "Pick your product and installment plan"], ["Verify Email", "Create account with secure email OTP"], ["Submit Request", "Fill the form and upload documents"], ["Get Approved", "Collect product & pay easy monthly installments"]].map(([t, d], k) => (
            <div key={k} className="rounded-xl border border-border bg-card p-6">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-primary font-display font-bold text-primary-foreground">{k + 1}</span>
              <h3 className="mt-4 font-display font-semibold">{t}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Latest */}
      {vis("latest") && data.latest.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 pb-8" data-testid="section-latest">
          <h2 className="font-display text-3xl font-bold tracking-tight lg:text-4xl">Latest Products</h2>
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
            {data.latest.map((p) => <ProductCard key={p.id} p={p} symbol={symbol} />)}
          </div>
        </section>
      )}

      {/* Team */}
      {vis("team") && data.team.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-16" data-testid="section-team">
          <span className="overline text-primary">Leadership</span>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight lg:text-4xl">Our Team</h2>
          <div className="mt-8 grid grid-cols-2 gap-5 md:grid-cols-4">
            {data.team.map((m) => (
              <div key={m.id} className="overflow-hidden rounded-xl border border-border bg-card text-center">
                <img src={m.photo} alt={m.name} className="h-52 w-full object-cover" />
                <div className="p-4"><p className="font-display font-semibold">{m.name}</p><p className="text-sm text-muted-foreground">{m.role}</p></div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-10">
        <div className="rounded-3xl bg-primary px-8 py-14 text-center text-primary-foreground">
          <h2 className="font-display text-3xl font-extrabold tracking-tight lg:text-4xl">Your Dreams, Our Deals</h2>
          <p className="mx-auto mt-3 max-w-lg text-primary-foreground/80">Own the latest products today and pay in easy monthly installments.</p>
          <Button asChild size="lg" variant="secondary" className="mt-6 rounded-full"><Link to="/products">Start Shopping<ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
        </div>
      </section>
    </PublicLayout>
  );
}
